import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAdminLayoutConfig,
  saveAdminLayoutConfig,
  resetAdminLayoutConfig,
  fetchBooths,
  LayoutConfigResponse,
  Booth,
} from '../../lib/api';
import {
  STALLS_DATA,
  FACILITIES_DATA,
  PILLARS_DATA,
  FLOORPLAN_DIMENSIONS,
  StallGeometry,
  FacilityZone,
  PillarMarker,
} from '../../data/floorplanData';
import { KonvaCanvas, checkBoundingBoxOverlap } from './KonvaCanvas';
import {
  Layers,
  Sparkles,
  Plus,
  Trash2,
  Copy,
  Save,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Eye,
  CheckCircle2,
  AlertCircle,
  Table as TableIcon,
  LayoutGrid,
  Filter,
  Search,
  Building2,
  MapPin,
  Settings,
  HelpCircle,
  Check,
  Shield,
  ShieldAlert,
  Magnet,
  Grid,
  Square,
  Circle as CircleIcon,
  Type,
  Box,
  Compass,
  Lock,
  Tag,
  Coins,
  Sliders,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Expand,
  Minimize,
  Download,
} from 'lucide-react';

interface FloorPlanBuilderProps {
  adminKey: string;
}

// Preset library definitions
export const STALL_PRESETS = [
  {
    idPrefix: 'SB',
    category: 'shell_9' as const,
    name: 'Standard Shell Scheme',
    dimensions: '3x3m',
    areaSqm: 9,
    priceInr: 85000,
    w: 29,
    h: 29,
    description: 'Fascia board, 1 table, 2 chairs, spotlights, 5A socket.',
    colorBadge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  {
    idPrefix: 'PC',
    category: 'premium_12' as const,
    name: 'Premium Corner Stall',
    dimensions: '4x3m',
    areaSqm: 12,
    priceInr: 115000,
    w: 38,
    h: 29,
    description: 'Two-side open corner booth, heightened brand visibility.',
    colorBadge: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  {
    idPrefix: 'GE',
    category: 'grand_18' as const,
    name: 'Grand Executive Pavilion',
    dimensions: '6x3m',
    areaSqm: 18,
    priceInr: 175000,
    w: 58,
    h: 29,
    description: 'Double frontage booth, executive discussion lounge.',
    colorBadge: 'bg-purple-50 text-purple-800 border-purple-200',
  },
  {
    idPrefix: 'IS',
    category: 'island_36' as const,
    name: 'Island Mega Pavilion',
    dimensions: '6x6m',
    areaSqm: 36,
    priceInr: 320000,
    w: 58,
    h: 58,
    description: 'Four-side open centerpiece, 360° attendee walk-around.',
    colorBadge: 'bg-amber-100 text-amber-900 border-amber-300',
  },
  {
    idPrefix: 'TT',
    category: 'trading_table' as const,
    name: 'B2B Trading Table',
    dimensions: '2x2m',
    areaSqm: 4,
    priceInr: 45000,
    w: 20,
    h: 20,
    description: 'Compact kiosk for startups, traders, and agritech consultants.',
    colorBadge: 'bg-green-50 text-green-800 border-green-200',
  },
  {
    idPrefix: 'MR',
    category: 'meeting_room' as const,
    name: 'VIP Meeting Chalet',
    dimensions: '6x4m',
    areaSqm: 24,
    priceInr: 200000,
    w: 58,
    h: 38,
    description: 'Sound-dampened meeting suite for high-value contract closures.',
    colorBadge: 'bg-blue-50 text-blue-800 border-blue-200',
  },
];

export const FACILITY_PRESETS = [
  {
    title: 'KEYNOTE AUDITORIUM',
    facilityType: 'conference' as const,
    dimensions: '15m x 10m',
    areaText: '150 sqm',
    w: 120,
    h: 80,
    colorBadge: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  },
  {
    title: 'AGRI CAFETERIA & DINING',
    facilityType: 'cafeteria' as const,
    dimensions: '12m x 8m',
    areaText: '96 sqm',
    w: 100,
    h: 70,
    colorBadge: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  {
    title: 'VIP BUYER NETWORKING',
    facilityType: 'organizer' as const,
    dimensions: '10m x 6m',
    areaText: '60 sqm',
    w: 80,
    h: 50,
    colorBadge: 'bg-purple-50 text-purple-800 border-purple-200',
  },
  {
    title: 'REGISTRATION & BADGING',
    facilityType: 'registration' as const,
    dimensions: '8m x 3m',
    areaText: '24 sqm',
    w: 70,
    h: 30,
    colorBadge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  {
    title: 'TURNSTILE ENTRY PORTAL',
    facilityType: 'turnstile' as const,
    dimensions: '6m x 3m',
    areaText: '18 sqm',
    w: 50,
    h: 25,
    colorBadge: 'bg-cyan-50 text-cyan-800 border-cyan-200',
  },
];

export function FloorPlanBuilder({ adminKey }: FloorPlanBuilderProps) {
  const queryClient = useQueryClient();
  const canvasViewportRef = useRef<HTMLDivElement>(null);

  // Fullscreen / Studio Mode state
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  // Collapsible Sidebars state (to maximize viewport width)
  const [isLibraryOpen, setIsLibraryOpen] = useState<boolean>(true);
  const [isInspectorOpen, setIsInspectorOpen] = useState<boolean>(true);

  // Mode: Visual Konva Canvas vs Spreadsheet Table
  const [viewMode, setViewMode] = useState<'visual' | 'table'>('visual');
  const [libraryTab, setLibraryTab] = useState<'stalls' | 'facilities' | 'database'>('stalls');

  // Canvas Settings
  const [zoomScale, setZoomScale] = useState<number>(1);
  const [snapGridSize, setSnapGridSize] = useState<number>(10);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [preventOverlap, setPreventOverlap] = useState<boolean>(true);

  // Selected element for Inspector
  const [selectedElement, setSelectedElement] = useState<{
    type: 'stall' | 'facility' | 'pillar';
    id: string;
  } | null>(null);

  // Filters & Notifications
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTableQuery, setSearchTableQuery] = useState('');
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Local draft state of layout
  const [localLayout, setLocalLayout] = useState<LayoutConfigResponse>({
    stalls: [...STALLS_DATA],
    facilities: [...FACILITIES_DATA],
    pillars: [...PILLARS_DATA],
    dimensions: { ...FLOORPLAN_DIMENSIONS },
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // TanStack Query: Fetch layout from server
  const { data: serverLayout, isLoading: isLayoutLoading } = useQuery<LayoutConfigResponse>({
    queryKey: ['admin-layout-config', adminKey],
    queryFn: () => fetchAdminLayoutConfig(adminKey),
    enabled: !!adminKey,
  });

  // TanStack Query: Fetch live booth availability database
  const { data: boothsList } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
  });

  // Map of boothId -> Booth record from live DB
  const boothsMap = useMemo(() => {
    const map: Record<string, Booth> = {};
    if (boothsList) {
      for (const b of boothsList) {
        map[b.id] = b;
      }
    }
    return map;
  }, [boothsList]);

  // Sync server data to local layout on initial load or refetch if not dirty
  useEffect(() => {
    if (serverLayout && !hasUnsavedChanges) {
      setLocalLayout({
        stalls: serverLayout.stalls || [...STALLS_DATA],
        facilities: serverLayout.facilities || [...FACILITIES_DATA],
        pillars: serverLayout.pillars || [...PILLARS_DATA],
        dimensions: serverLayout.dimensions || { ...FLOORPLAN_DIMENSIONS },
      });
    }
  }, [serverLayout, hasUnsavedChanges]);

  // Auto-open inspector when an element is selected
  useEffect(() => {
    if (selectedElement) {
      setIsInspectorOpen(true);
    }
  }, [selectedElement]);

  // Handle keyboard shortcuts (Esc to exit fullscreen, etc.)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else if (selectedElement) {
          setSelectedElement(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, selectedElement]);

  // Mutation: Save layout to database
  const saveMutation = useMutation({
    mutationFn: (data: Partial<LayoutConfigResponse>) => saveAdminLayoutConfig(data, adminKey),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      setStatusMessage({
        type: 'success',
        text: 'Expo floor layout saved successfully and synced with the availability database!',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-layout-config'] });
      queryClient.invalidateQueries({ queryKey: ['public-floorplan-layout'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      setTimeout(() => setStatusMessage(null), 4000);
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to save layout changes' });
    },
  });

  // Mutation: Reset layout to architectural default
  const resetMutation = useMutation({
    mutationFn: () => resetAdminLayoutConfig(adminKey),
    onSuccess: (data) => {
      setLocalLayout(data);
      setHasUnsavedChanges(false);
      setSelectedElement(null);
      setStatusMessage({
        type: 'success',
        text: 'Floor layout restored to default Pavilion 3 architectural blueprint',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-layout-config'] });
      queryClient.invalidateQueries({ queryKey: ['public-floorplan-layout'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      setTimeout(() => setStatusMessage(null), 4000);
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to reset layout' });
    },
  });

  // Currently selected stall or facility
  const selectedStall = useMemo(() => {
    if (selectedElement?.type !== 'stall') return null;
    return localLayout.stalls.find((s) => s.id === selectedElement.id) || null;
  }, [selectedElement, localLayout.stalls]);

  const selectedFacility = useMemo(() => {
    if (selectedElement?.type !== 'facility') return null;
    return localLayout.facilities.find((f) => f.id === selectedElement.id) || null;
  }, [selectedElement, localLayout.facilities]);

  // Fit to Viewport: calculates optimal zoom scale based on available container dimensions
  const handleFitToViewport = () => {
    if (canvasViewportRef.current) {
      const { clientWidth, clientHeight } = canvasViewportRef.current;
      const padding = 60;
      const scaleX = (clientWidth - padding) / 724;
      const scaleY = (clientHeight - padding) / 920;
      const fitScale = Math.min(scaleX, scaleY, 1.3);
      setZoomScale(Number(Math.max(0.45, fitScale).toFixed(2)));
      setStatusMessage({
        type: 'success',
        text: `Canvas fitted to viewport (${Math.round(fitScale * 100)}%)`,
      });
      setTimeout(() => setStatusMessage(null), 2500);
    }
  };

  // Helper: Find open non-overlapping grid coordinate
  const findOpenGridPosition = (w: number, h: number): { x: number; y: number } => {
    const hallBounds = localLayout.dimensions.hallBounds || { x: 42, y: 170, w: 640, h: 710 };
    const step = 20;

    for (let y = hallBounds.y + 10; y <= hallBounds.y + hallBounds.h - h - 10; y += step) {
      for (let x = hallBounds.x + 10; x <= hallBounds.x + hallBounds.w - w - 10; x += step) {
        let collides = false;
        for (const s of localLayout.stalls) {
          if (checkBoundingBoxOverlap({ x, y, w, h }, { x: s.x, y: s.y, w: s.w, h: s.h })) {
            collides = true;
            break;
          }
        }
        if (!collides) {
          return { x, y };
        }
      }
    }
    return { x: 300, y: 350 };
  };

  // Position updates from Konva Canvas
  const handleUpdateStallPosition = (id: string, x: number, y: number): boolean => {
    setLocalLayout((prev) => ({
      ...prev,
      stalls: prev.stalls.map((s) => (s.id === id ? { ...s, x, y } : s)),
    }));
    setHasUnsavedChanges(true);
    return true;
  };

  const handleUpdateFacilityPosition = (id: string, x: number, y: number): boolean => {
    setLocalLayout((prev) => ({
      ...prev,
      facilities: prev.facilities.map((f) => (f.id === id ? { ...f, x, y } : f)),
    }));
    setHasUnsavedChanges(true);
    return true;
  };

  // Handle overlap rejection with alert
  const handleOverlapRejection = (draggedId: string, collidedWithId: string) => {
    setStatusMessage({
      type: 'error',
      text: `⚠️ Overlap prevented! Stall ${draggedId} cannot overlap with Stall ${collidedWithId}. Position was reverted.`,
    });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // Drop or 1-click add from Library
  const handleDropFromLibrary = (presetData: any, x: number, y: number) => {
    if (presetData.category) {
      // Stall preset
      const nextNum = localLayout.stalls.length + 1;
      const stallId = presetData.id || `${presetData.idPrefix || 'SB'}-${nextNum}`;

      if (preventOverlap) {
        for (const s of localLayout.stalls) {
          if (checkBoundingBoxOverlap({ x, y, w: presetData.w, h: presetData.h }, { x: s.x, y: s.y, w: s.w, h: s.h })) {
            setStatusMessage({
              type: 'error',
              text: `⚠️ Cannot place ${stallId} here: overlaps with existing Stall ${s.id}. Try an open aisle slot.`,
            });
            setTimeout(() => setStatusMessage(null), 4000);
            return;
          }
        }
      }

      const created: StallGeometry = {
        id: stallId,
        dimensions: presetData.dimensions || '3x3m',
        areaSqm: presetData.areaSqm || 9,
        priceInr: presetData.priceInr || 85000,
        category: presetData.category,
        x,
        y,
        w: presetData.w || 29,
        h: presetData.h || 29,
        initialStatus: 'available',
      };

      setLocalLayout((prev) => ({
        ...prev,
        stalls: [...prev.stalls, created],
      }));
      setSelectedElement({ type: 'stall', id: created.id });
      setHasUnsavedChanges(true);
      setStatusMessage({
        type: 'success',
        text: `Stall ${created.id} (${created.dimensions}) added to expo floor!`,
      });
      setTimeout(() => setStatusMessage(null), 3000);
    } else {
      // Facility preset
      const facId = presetData.id || `fac-${Date.now().toString().slice(-4)}`;
      const created: FacilityZone = {
        id: facId,
        title: presetData.title || 'NEW ZONE',
        dimensions: presetData.dimensions || '10m x 8m',
        areaText: presetData.areaText || '80 sqm',
        x,
        y,
        w: presetData.w || 90,
        h: presetData.h || 50,
        type: presetData.facilityType || 'conference',
      };

      setLocalLayout((prev) => ({
        ...prev,
        facilities: [...prev.facilities, created],
      }));
      setSelectedElement({ type: 'facility', id: created.id });
      setHasUnsavedChanges(true);
      setStatusMessage({
        type: 'success',
        text: `Facility "${created.title}" placed on canvas!`,
      });
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };

  // Quick 1-click Add Preset button (auto-finds open slot)
  const handleQuickAddPreset = (preset: typeof STALL_PRESETS[0]) => {
    const pos = findOpenGridPosition(preset.w, preset.h);
    handleDropFromLibrary(preset, pos.x, pos.y);
  };

  const handleQuickAddFacility = (preset: typeof FACILITY_PRESETS[0]) => {
    const pos = findOpenGridPosition(preset.w, preset.h);
    handleDropFromLibrary(preset, pos.x, pos.y);
  };

  // Full Layout Overlap Scanner & Validator
  const handleValidateLayout = () => {
    const collisions: Array<[string, string]> = [];
    const stalls = localLayout.stalls;

    for (let i = 0; i < stalls.length; i++) {
      for (let j = i + 1; j < stalls.length; j++) {
        if (checkBoundingBoxOverlap(stalls[i], stalls[j])) {
          collisions.push([stalls[i].id, stalls[j].id]);
        }
      }
    }

    if (collisions.length === 0) {
      setStatusMessage({
        type: 'success',
        text: `✅ Floor plan validation passed! All ${stalls.length} stalls have 0 overlaps and clear passageways.`,
      });
    } else {
      setStatusMessage({
        type: 'error',
        text: `⚠️ Collision detected between: ${collisions.slice(0, 3).map(([a, b]) => `${a} & ${b}`).join(', ')}${collisions.length > 3 ? '...' : ''}. Please resolve before final blueprint lock.`,
      });
    }
    setTimeout(() => setStatusMessage(null), 6000);
  };

  // Nudge Position from Inspector
  const nudgePosition = (dx: number, dy: number) => {
    if (selectedElement?.type === 'stall' && selectedStall) {
      const newX = Math.max(0, Math.round(selectedStall.x + dx));
      const newY = Math.max(0, Math.round(selectedStall.y + dy));

      if (preventOverlap) {
        for (const s of localLayout.stalls) {
          if (s.id !== selectedStall.id && checkBoundingBoxOverlap({ x: newX, y: newY, w: selectedStall.w, h: selectedStall.h }, s)) {
            setStatusMessage({ type: 'error', text: `Nudge blocked: overlaps with Stall ${s.id}` });
            setTimeout(() => setStatusMessage(null), 2500);
            return;
          }
        }
      }

      setLocalLayout((prev) => ({
        ...prev,
        stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, x: newX, y: newY } : s)),
      }));
      setHasUnsavedChanges(true);
    } else if (selectedElement?.type === 'facility' && selectedFacility) {
      setLocalLayout((prev) => ({
        ...prev,
        facilities: prev.facilities.map((f) =>
          f.id === selectedFacility.id
            ? { ...f, x: Math.max(0, Math.round(f.x + dx)), y: Math.max(0, Math.round(f.y + dy)) }
            : f
        ),
      }));
      setHasUnsavedChanges(true);
    }
  };

  // Delete stall with booked safety lock
  const handleDeleteStall = (id: string) => {
    const liveBooth = boothsMap[id];
    if (liveBooth?.status === 'booked') {
      if (
        !window.confirm(
          `⚠️ WARNING: Stall ${id} is actively BOOKED by exhibitor "${liveBooth.companyName || 'Confirmed Buyer'}". Deleting it will disrupt live exhibitor booking records. Proceed anyway?`
        )
      ) {
        return;
      }
    } else {
      if (!window.confirm(`Delete Stall ${id} from the layout?`)) return;
    }

    setLocalLayout((prev) => ({
      ...prev,
      stalls: prev.stalls.filter((s) => s.id !== id),
    }));
    if (selectedElement?.id === id) setSelectedElement(null);
    setHasUnsavedChanges(true);
  };

  // Duplicate stall
  const handleDuplicateStall = (stall: StallGeometry) => {
    const nextNum = localLayout.stalls.length + 1;
    const newId = `${stall.id}-COPY${nextNum}`;
    const targetPos = findOpenGridPosition(stall.w, stall.h);

    const duplicated: StallGeometry = {
      ...stall,
      id: newId,
      x: targetPos.x,
      y: targetPos.y,
      initialStatus: 'available',
    };

    setLocalLayout((prev) => ({
      ...prev,
      stalls: [...prev.stalls, duplicated],
    }));
    setSelectedElement({ type: 'stall', id: newId });
    setHasUnsavedChanges(true);
    setStatusMessage({ type: 'success', text: `Stall duplicated as ${newId} at open slot!` });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  // Summary counts
  const totalStallsCount = localLayout.stalls.length;
  const availableCount = localLayout.stalls.filter((s) => (boothsMap[s.id]?.status || s.initialStatus) === 'available').length;
  const bookedCount = localLayout.stalls.filter((s) => (boothsMap[s.id]?.status || s.initialStatus) === 'booked').length;
  const onHoldCount = localLayout.stalls.filter((s) => (boothsMap[s.id]?.status || s.initialStatus) === 'on_hold').length;
  const totalAreaSqm = localLayout.stalls.reduce((sum, s) => sum + (Number(s.areaSqm) || 0), 0);

  // Filtered stalls for Table view
  const filteredStalls = useMemo(() => {
    return localLayout.stalls.filter((s) => {
      const matchesCat = filterCategory === 'all' || s.category === filterCategory;
      const matchesSearch =
        searchTableQuery === '' ||
        s.id.toLowerCase().includes(searchTableQuery.toLowerCase()) ||
        s.dimensions.toLowerCase().includes(searchTableQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [localLayout.stalls, filterCategory, searchTableQuery]);

  return (
    <div
      className={
        isFullScreen
          ? 'fixed inset-0 z-50 bg-[#0F172A] text-slate-100 flex flex-col w-screen h-screen overflow-hidden select-none p-3 space-y-2'
          : 'space-y-3 w-full'
      }
    >
      {/* 1. TOP STUDIO COMMAND BAR */}
      <div
        className={`flex items-center justify-between gap-3 px-4 py-3 shrink-0 border-b transition-colors ${
          isFullScreen
            ? 'bg-[#1E293B] border-slate-700/80 shadow-md'
            : 'bg-white border-slate-200 rounded-2xl shadow-xs'
        }`}
      >
        {/* Left: Studio Title & Mode Tabs */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#0A3D27] flex items-center justify-center text-white shadow-xs">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-black tracking-tight ${isFullScreen ? 'text-white' : 'text-slate-900'}`}>
                  Pavilion 3 Floor Plan Studio
                </span>
                {hasUnsavedChanges && (
                  <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse">
                    Unsaved
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                <span>{totalStallsCount} Stalls</span>
                <span>•</span>
                <span className="text-emerald-400 font-bold">{availableCount} Free</span>
                <span>•</span>
                <span className="text-slate-400 font-bold">{bookedCount} Booked</span>
                <span>•</span>
                <span>{totalAreaSqm.toLocaleString()} sqm</span>
              </div>
            </div>
          </div>

          {/* Mode Switcher */}
          <div className={`hidden md:flex items-center p-0.5 rounded-xl border ${isFullScreen ? 'bg-slate-800/80 border-slate-700' : 'bg-[#F8F9F6] border-slate-200'}`}>
            <button
              type="button"
              onClick={() => setViewMode('visual')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'visual'
                  ? 'bg-[#0A3D27] text-white shadow-xs font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Canvas</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-[#0A3D27] text-white shadow-xs font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <TableIcon className="w-3.5 h-3.5" />
              <span>Spreadsheet</span>
            </button>
          </div>
        </div>

        {/* Center: Status Message Toast */}
        {statusMessage && (
          <div
            className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold transition-all max-w-md truncate border ${
              statusMessage.type === 'success'
                ? 'bg-emerald-950/80 text-emerald-200 border-emerald-500/40'
                : 'bg-rose-950/80 text-rose-200 border-rose-500/40'
            }`}
          >
            {statusMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span className="truncate">{statusMessage.text}</span>
          </div>
        )}

        {/* Right: Actions, Fullscreen toggle, Save */}
        <div className="flex items-center gap-2">
          {/* Validate Clearance */}
          <button
            type="button"
            onClick={handleValidateLayout}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 cursor-pointer ${
              isFullScreen
                ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
            }`}
            title="Scan floor plan for any overlapping stalls"
          >
            <Shield className="w-3.5 h-3.5 text-emerald-500" />
            <span className="hidden sm:inline">Validate</span>
          </button>

          {/* Reset Blueprint */}
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Reset layout back to Pavilion 3 default architectural blueprint?')) {
                resetMutation.mutate();
              }
            }}
            disabled={resetMutation.isPending}
            className={`p-1.5 sm:px-3 sm:py-1.5 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 cursor-pointer ${
              isFullScreen
                ? 'bg-rose-950/50 hover:bg-rose-900/60 text-rose-300 border-rose-800/60'
                : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200'
            }`}
            title="Reset to default layout"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Reset</span>
          </button>

          {/* Fullscreen / Full Viewport Toggle */}
          <button
            type="button"
            onClick={() => {
              setIsFullScreen(!isFullScreen);
              setTimeout(() => handleFitToViewport(), 150);
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-colors flex items-center gap-1.5 cursor-pointer ${
              isFullScreen
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500 shadow-sm'
                : 'bg-[#FAF5EA] hover:bg-[#F5E8C7] text-[#785613] border-[#E6C687]'
            }`}
            title={isFullScreen ? 'Exit Full Viewport (Esc)' : 'Maximize to Full Viewport'}
          >
            {isFullScreen ? (
              <>
                <Minimize2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Exit Viewport</span>
              </>
            ) : (
              <>
                <Maximize2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Full Viewport</span>
              </>
            )}
          </button>

          {/* Save & Sync DB */}
          <button
            type="button"
            onClick={() => saveMutation.mutate(localLayout)}
            disabled={saveMutation.isPending || !hasUnsavedChanges}
            className={`px-4 py-1.5 text-xs font-black rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer ${
              hasUnsavedChanges
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white animate-pulse'
                : isFullScreen
                ? 'bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed'
                : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
            }`}
          >
            <Save className="w-3.5 h-3.5" />
            <span>{saveMutation.isPending ? 'Syncing...' : 'Save & Sync DB'}</span>
          </button>
        </div>
      </div>

      {/* 2. MAIN STUDIO WORKSPACE (FULL-WIDTH CANVAS WITH TOP LIBRARY & BOTTOM INSPECTOR) */}
      {viewMode === 'visual' ? (
        <div className="flex flex-col space-y-3 w-full">
          {/* ======================================================== */}
          {/* A. COMPONENT LIBRARY (ABOVE THE CANVAS)                 */}
          {/* ======================================================== */}
          <div
            className={`w-full border rounded-2xl transition-all shadow-xs overflow-hidden ${
              isFullScreen
                ? 'bg-[#1E293B] border-slate-700/80 text-slate-200'
                : 'bg-white border-slate-200 text-slate-800'
            }`}
          >
            {/* Library Top Bar: Title, Tabs & Collapse/Expand Button */}
            <div className="px-4 py-2.5 border-b border-slate-200/60 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center justify-center">
                    <Box className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-xs font-black uppercase tracking-wider">Component Library</span>
                    <span className="hidden sm:inline text-[11px] text-slate-400 ml-2">Drag onto canvas or click + to add</span>
                  </div>
                </div>

                {/* Library Tabs */}
                <div className={`flex items-center p-0.5 rounded-xl border text-[11px] font-bold ${
                  isFullScreen ? 'bg-slate-800/80 border-slate-700' : 'bg-[#F8F9F6] border-slate-200'
                }`}>
                  <button
                    type="button"
                    onClick={() => {
                      setLibraryTab('stalls');
                      setIsLibraryOpen(true);
                    }}
                    className={`px-3 py-1 rounded-lg transition-all cursor-pointer ${
                      libraryTab === 'stalls' && isLibraryOpen
                        ? 'bg-emerald-600 text-white font-black shadow-xs'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Stall Presets ({STALL_PRESETS.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLibraryTab('facilities');
                      setIsLibraryOpen(true);
                    }}
                    className={`px-3 py-1 rounded-lg transition-all cursor-pointer ${
                      libraryTab === 'facilities' && isLibraryOpen
                        ? 'bg-emerald-600 text-white font-black shadow-xs'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Facilities ({FACILITY_PRESETS.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLibraryTab('database');
                      setIsLibraryOpen(true);
                    }}
                    className={`px-3 py-1 rounded-lg transition-all cursor-pointer ${
                      libraryTab === 'database' && isLibraryOpen
                        ? 'bg-emerald-600 text-white font-black shadow-xs'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    DB Live Roster ({localLayout.stalls.length})
                  </button>
                </div>
              </div>

              {/* Collapse / Expand Toggle */}
              <button
                type="button"
                onClick={() => setIsLibraryOpen(!isLibraryOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border transition-colors cursor-pointer ${
                  isFullScreen
                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-slate-700'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200'
                }`}
                title={isLibraryOpen ? 'Collapse library to maximize canvas' : 'Expand component library'}
              >
                {isLibraryOpen ? (
                  <>
                    <ChevronUp className="w-3.5 h-3.5" />
                    <span>Hide Library</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3.5 h-3.5" />
                    <span>Show Library</span>
                  </>
                )}
              </button>
            </div>

            {/* Library Content Tray (When Open) */}
            {isLibraryOpen && (
              <div className="p-3">
                {/* 1. Stalls Tab */}
                {libraryTab === 'stalls' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
                    {STALL_PRESETS.map((preset) => (
                      <div
                        key={preset.category}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/json', JSON.stringify({ ...preset, type: 'stall' }));
                        }}
                        className={`p-2.5 rounded-xl border transition-all cursor-grab active:cursor-grabbing hover:shadow-xs group flex flex-col justify-between ${
                          isFullScreen
                            ? 'bg-slate-800/60 border-slate-700 hover:border-emerald-500'
                            : 'bg-[#F8F9F6] border-slate-200 hover:border-[#0A3D27] hover:bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1 mb-1.5">
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${preset.colorBadge}`}>
                            {preset.dimensions}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuickAddPreset(preset)}
                            className="p-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500 text-emerald-600 dark:text-emerald-300 hover:text-white transition-colors cursor-pointer"
                            title="Auto-place on canvas"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div>
                          <p className={`text-xs font-black truncate ${isFullScreen ? 'text-white' : 'text-slate-900'}`}>
                            {preset.name}
                          </p>
                          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
                            <span>{preset.areaSqm} sqm</span>
                            <span className="font-mono font-bold text-emerald-500">₹{(preset.priceInr / 1000).toFixed(0)}k</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 2. Facilities Tab */}
                {libraryTab === 'facilities' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                    {FACILITY_PRESETS.map((preset, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/json', JSON.stringify({ ...preset, type: 'facility' }));
                        }}
                        className={`p-2.5 rounded-xl border transition-all cursor-grab active:cursor-grabbing hover:shadow-xs group flex flex-col justify-between ${
                          isFullScreen
                            ? 'bg-slate-800/60 border-slate-700 hover:border-indigo-500'
                            : 'bg-[#F8F9F6] border-slate-200 hover:border-indigo-600 hover:bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1 mb-1.5">
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${preset.colorBadge}`}>
                            {preset.facilityType.toUpperCase()}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuickAddFacility(preset)}
                            className="p-1 rounded-lg bg-indigo-500/20 hover:bg-indigo-500 text-indigo-400 hover:text-white transition-colors cursor-pointer"
                            title="Auto-place facility"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div>
                          <p className={`text-xs font-black truncate ${isFullScreen ? 'text-white' : 'text-slate-900'}`}>
                            {preset.title}
                          </p>
                          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
                            <span>{preset.dimensions}</span>
                            <span>{preset.areaText}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 3. DB Live Roster Tab */}
                {libraryTab === 'database' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search stall ID in DB roster..."
                          value={searchTableQuery}
                          onChange={(e) => setSearchTableQuery(e.target.value)}
                          className={`w-full pl-8 pr-2.5 py-1 text-xs rounded-lg border focus:outline-none ${
                            isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-[#F8F9F6] border-slate-200'
                          }`}
                        />
                      </div>
                      <span className="text-[11px] text-slate-400 font-mono">
                        Showing {filteredStalls.length} of {localLayout.stalls.length} stalls (Click any to inspect & focus)
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin max-h-24 flex-wrap">
                      {filteredStalls.slice(0, 48).map((stall) => {
                        const liveBooth = boothsMap[stall.id];
                        const status = liveBooth?.status || stall.initialStatus || 'available';
                        const isSelected = selectedElement?.id === stall.id;

                        return (
                          <button
                            key={stall.id}
                            type="button"
                            onClick={() => {
                              setSelectedElement({ type: 'stall', id: stall.id });
                            }}
                            className={`px-2 py-1 rounded-lg border text-xs font-mono font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-emerald-600 text-white border-emerald-500 ring-2 ring-emerald-400/30'
                                : isFullScreen
                                ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                                : 'bg-[#F8F9F6] hover:bg-slate-100 text-slate-700 border-slate-200'
                            }`}
                          >
                            <span>{stall.id}</span>
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                status === 'booked'
                                  ? 'bg-slate-400'
                                  : status === 'on_hold'
                                  ? 'bg-amber-400'
                                  : 'bg-emerald-400'
                              }`}
                            />
                          </button>
                        );
                      })}
                      {filteredStalls.length > 48 && (
                        <span className="text-[11px] text-slate-400 px-2 py-1">
                          +{filteredStalls.length - 48} more (filter to refine)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ======================================================== */}
          {/* B. CANVAS SECTION (FULL WIDTH OF THE PAGE)               */}
          {/* ======================================================== */}
          <div
            ref={canvasViewportRef}
            className={`w-full relative flex flex-col overflow-hidden rounded-2xl border shadow-sm ${
              isFullScreen
                ? 'flex-1 h-[calc(100vh-320px)] min-h-[500px] bg-[#0B132B] border-slate-700'
                : 'h-[720px] bg-[#CBD5E1] border-slate-300'
            }`}
          >
            {/* FLOATING TOP-CENTER TOOLBAR PILL */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-slate-900/90 text-white backdrop-blur-md px-3 py-1.5 rounded-2xl shadow-xl border border-slate-700/80">
              {/* Toggle Grid */}
              <button
                type="button"
                onClick={() => setShowGrid(!showGrid)}
                className={`p-1.5 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer transition-colors ${
                  showGrid ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                title="Toggle Grid (G)"
              >
                <Grid className="w-3.5 h-3.5" />
                <span className="text-[11px] hidden md:inline">Grid</span>
              </button>

              {/* Snap Step */}
              <div className="flex items-center gap-0.5 bg-slate-800 p-0.5 rounded-lg border border-slate-700 text-[10px]">
                <Magnet className="w-3 h-3 text-emerald-400 ml-1" />
                <button
                  type="button"
                  onClick={() => setSnapGridSize(5)}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer ${
                    snapGridSize === 5 ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  5p
                </button>
                <button
                  type="button"
                  onClick={() => setSnapGridSize(10)}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer ${
                    snapGridSize === 10 ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  10p
                </button>
                <button
                  type="button"
                  onClick={() => setSnapGridSize(20)}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer ${
                    snapGridSize === 20 ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  20p
                </button>
              </div>

              {/* Anti-Overlap Shield Toggle */}
              <button
                type="button"
                onClick={() => setPreventOverlap(!preventOverlap)}
                className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 cursor-pointer transition-colors ${
                  preventOverlap
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                    : 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                }`}
                title="Strict Overlap Prevention Toggle"
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{preventOverlap ? 'Anti-Overlap ON' : 'Anti-Overlap OFF'}</span>
              </button>

              <div className="w-px h-4 bg-slate-700 mx-1" />

              {/* Zoom Controls */}
              <button
                type="button"
                onClick={() => setZoomScale((prev) => Math.max(0.4, Number((prev - 0.1).toFixed(2))))}
                className="p-1 text-slate-400 hover:text-white rounded cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-[10px] font-mono font-bold w-10 text-center text-slate-300">
                {Math.round(zoomScale * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoomScale((prev) => Math.min(2, Number((prev + 0.1).toFixed(2))))}
                className="p-1 text-slate-400 hover:text-white rounded cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>

              {/* Fit to Viewport */}
              <button
                type="button"
                onClick={handleFitToViewport}
                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-[10px] font-bold flex items-center gap-1 cursor-pointer"
                title="Fit Canvas to Available Viewport"
              >
                <Expand className="w-3 h-3 text-emerald-400" />
                <span className="hidden sm:inline">Fit Viewport</span>
              </button>
            </div>

            {/* FLOATING BOTTOM-LEFT LEGEND PILL */}
            <div className="absolute bottom-3 left-4 z-20 hidden md:flex items-center gap-3 bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-mono px-3 py-1.5 rounded-xl border border-slate-700 shadow-lg">
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#ECFDF5] border border-[#059669]" />
                <span>Shell 9m²</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#FEF3C7] border border-[#D97706]" />
                <span>Corner 12m²</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#F5F3FF] border border-[#7C3AED]" />
                <span>Executive 18m²</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#FFFBEB] border border-[#B45309]" />
                <span>Island 36m²</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#F1F5F9] border border-[#64748B]" />
                <span>Booked</span>
              </div>
            </div>

            {/* THE KONVA CANVAS (FULL WIDTH OF THE PAGE) */}
            <div className="flex-1 w-full h-full overflow-hidden">
              <KonvaCanvas
                stalls={localLayout.stalls}
                facilities={localLayout.facilities}
                pillars={localLayout.pillars}
                dimensions={localLayout.dimensions}
                selectedElement={selectedElement}
                onSelectElement={(type, id) => setSelectedElement({ type, id })}
                onUpdateStallPosition={handleUpdateStallPosition}
                onUpdateFacilityPosition={handleUpdateFacilityPosition}
                onDropFromLibrary={handleDropFromLibrary}
                onOverlapRejection={handleOverlapRejection}
                boothsMap={boothsMap}
                zoomScale={zoomScale}
                snapGridSize={snapGridSize}
                showGrid={showGrid}
                preventOverlap={preventOverlap}
                containerClassName="w-full h-full rounded-none border-none bg-slate-200"
              />
            </div>
          </div>

          {/* ======================================================== */}
          {/* C. INSPECTOR (BELOW THE CANVAS)                          */}
          {/* ======================================================== */}
          <div
            className={`w-full border rounded-2xl transition-all shadow-xs p-3 sm:p-4 ${
              isFullScreen
                ? 'bg-[#1E293B] border-slate-700/80 text-slate-200'
                : 'bg-white border-slate-200 text-slate-800'
            }`}
          >
            {selectedStall ? (
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* 1. Identity & Live Status */}
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`p-2.5 rounded-xl border ${isFullScreen ? 'bg-slate-800 border-slate-700' : 'bg-[#F8F9F6] border-slate-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-black font-mono">{selectedStall.id}</span>
                      <span
                        className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                          boothsMap[selectedStall.id]?.status === 'booked'
                            ? 'bg-slate-700 text-slate-200 border-slate-600'
                            : boothsMap[selectedStall.id]?.status === 'on_hold'
                            ? 'bg-amber-950 text-amber-300 border-amber-700'
                            : 'bg-emerald-950 text-emerald-300 border-emerald-700'
                        }`}
                      >
                        {boothsMap[selectedStall.id]?.status || selectedStall.initialStatus || 'available'}
                      </span>
                    </div>
                    {boothsMap[selectedStall.id]?.companyName ? (
                      <p className="text-xs text-emerald-500 font-bold mt-1 flex items-center gap-1 max-w-[180px] truncate">
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{boothsMap[selectedStall.id]?.companyName}</span>
                      </p>
                    ) : (
                      <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                        {selectedStall.dimensions} • {selectedStall.areaSqm} sqm
                      </p>
                    )}
                  </div>
                </div>

                {/* 2. Coordinates & Dimensions */}
                <div className="grid grid-cols-4 gap-2 text-xs shrink-0 max-w-sm">
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">X Pos</label>
                    <input
                      type="number"
                      value={selectedStall.x}
                      onChange={(e) => handleUpdateStallPosition(selectedStall.id, Number(e.target.value), selectedStall.y)}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Y Pos</label>
                    <input
                      type="number"
                      value={selectedStall.y}
                      onChange={(e) => handleUpdateStallPosition(selectedStall.id, selectedStall.x, Number(e.target.value))}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Width</label>
                    <input
                      type="number"
                      value={selectedStall.w}
                      onChange={(e) => {
                        setLocalLayout((prev) => ({
                          ...prev,
                          stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, w: Number(e.target.value) } : s)),
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Height</label>
                    <input
                      type="number"
                      value={selectedStall.h}
                      onChange={(e) => {
                        setLocalLayout((prev) => ({
                          ...prev,
                          stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, h: Number(e.target.value) } : s)),
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                </div>

                {/* 3. Precision Nudge Compass */}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-[10px] font-bold text-slate-400 uppercase">Nudge:</div>
                  <div className="grid grid-cols-3 gap-0.5 w-[76px]">
                    <div />
                    <button
                      type="button"
                      onClick={() => nudgePosition(0, -snapGridSize)}
                      className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center cursor-pointer"
                      title="Nudge Up"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <div />
                    <button
                      type="button"
                      onClick={() => nudgePosition(-snapGridSize, 0)}
                      className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center cursor-pointer"
                      title="Nudge Left"
                    >
                      <ArrowLeft className="w-3 h-3" />
                    </button>
                    <div className="flex items-center justify-center text-[9px] font-mono text-slate-400">
                      {snapGridSize}p
                    </div>
                    <button
                      type="button"
                      onClick={() => nudgePosition(snapGridSize, 0)}
                      className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center cursor-pointer"
                      title="Nudge Right"
                    >
                      <ArrowRight className="w-3 h-3" />
                    </button>
                    <div />
                    <button
                      type="button"
                      onClick={() => nudgePosition(0, snapGridSize)}
                      className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center justify-center cursor-pointer"
                      title="Nudge Down"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    <div />
                  </div>
                </div>

                {/* 4. Category & Commercial Pricing */}
                <div className="flex items-center gap-2 flex-wrap text-xs shrink-0">
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase block">Category</label>
                    <select
                      value={selectedStall.category}
                      onChange={(e) => {
                        setLocalLayout((prev) => ({
                          ...prev,
                          stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, category: e.target.value as any } : s)),
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      className={`mt-0.5 px-2 py-1 rounded-lg text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    >
                      <option value="shell_9">Shell (3x3m - 9sqm)</option>
                      <option value="premium_12">Corner (4x3m - 12sqm)</option>
                      <option value="grand_18">Executive (6x3m - 18sqm)</option>
                      <option value="island_36">Island (6x6m - 36sqm)</option>
                      <option value="trading_table">Trading Table (2x2m)</option>
                      <option value="meeting_room">Meeting Room (6x4m)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase block">Area (sqm)</label>
                    <input
                      type="number"
                      value={selectedStall.areaSqm}
                      onChange={(e) => {
                        setLocalLayout((prev) => ({
                          ...prev,
                          stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, areaSqm: Number(e.target.value) } : s)),
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-20 mt-0.5 px-2 py-1 rounded-lg text-xs font-mono border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase block">Price (₹ INR)</label>
                    <input
                      type="number"
                      value={selectedStall.priceInr}
                      onChange={(e) => {
                        setLocalLayout((prev) => ({
                          ...prev,
                          stalls: prev.stalls.map((s) => (s.id === selectedStall.id ? { ...s, priceInr: Number(e.target.value) } : s)),
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      className={`w-24 mt-0.5 px-2 py-1 rounded-lg text-xs font-mono border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                </div>

                {/* 5. Stall Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDuplicateStall(selectedStall)}
                    className={`py-1.5 px-3 text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer ${
                      isFullScreen
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Duplicate</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteStall(selectedStall.id)}
                    className="py-1.5 px-3 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedElement(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-500/20 cursor-pointer"
                    title="Close Inspector"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : selectedFacility ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl border ${isFullScreen ? 'bg-slate-800 border-slate-700' : 'bg-[#F8F9F6] border-slate-200'}`}>
                    <span className="text-sm font-black">{selectedFacility.title}</span>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{selectedFacility.dimensions} • {selectedFacility.areaText}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs max-w-xs">
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">X Position</label>
                    <input
                      type="number"
                      value={selectedFacility.x}
                      onChange={(e) => handleUpdateFacilityPosition(selectedFacility.id, Number(e.target.value), selectedFacility.y)}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Y Position</label>
                    <input
                      type="number"
                      value={selectedFacility.y}
                      onChange={(e) => handleUpdateFacilityPosition(selectedFacility.id, selectedFacility.x, Number(e.target.value))}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg font-mono text-xs border focus:outline-none ${
                        isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                      }`}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Delete facility "${selectedFacility.title}"?`)) {
                        setLocalLayout((prev) => ({
                          ...prev,
                          facilities: prev.facilities.filter((f) => f.id !== selectedFacility.id),
                        }));
                        setSelectedElement(null);
                        setHasUnsavedChanges(true);
                      }
                    }}
                    className="py-1.5 px-3 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Delete Facility</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedElement(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-500/20 cursor-pointer"
                    title="Close Inspector"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between text-xs text-slate-400 py-1">
                <div className="flex items-center gap-2">
                  <Compass className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold">
                    Inspector: Click on any stall, facility, or zone on the canvas above to inspect coordinates, dimensions, category, and pricing.
                  </span>
                </div>
                <div className="hidden sm:flex items-center gap-2 font-mono text-[11px]">
                  <span>Hall: {localLayout.dimensions.width}px × {localLayout.dimensions.height}px</span>
                  <span>•</span>
                  <span>Grid: {snapGridSize}px</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* SPREADSHEET TABLE VIEW */
        <div className={`border rounded-2xl p-5 shadow-xs space-y-4 ${isFullScreen ? 'bg-[#1E293B] border-slate-700 text-slate-100 flex-1' : 'bg-white border-slate-200'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search stalls by ID, dimensions..."
                value={searchTableQuery}
                onChange={(e) => setSearchTableQuery(e.target.value)}
                className={`px-3 py-1.5 border rounded-lg text-xs w-64 focus:outline-none ${
                  isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                }`}
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className={`px-3 py-1.5 border rounded-lg text-xs focus:outline-none ${
                  isFullScreen ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                }`}
              >
                <option value="all">All Categories</option>
                <option value="shell_9">Shell 9sqm</option>
                <option value="premium_12">Premium 12sqm</option>
                <option value="grand_18">Grand 18sqm</option>
                <option value="island_36">Island 36sqm</option>
                <option value="trading_table">Trading Table</option>
                <option value="meeting_room">Meeting Room</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] border border-slate-200/40 rounded-xl scrollbar-thin">
            <table className="w-full text-left text-xs">
              <thead className={`border-b font-bold text-[11px] sticky top-0 ${isFullScreen ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-[#F8F9F6] border-slate-200 text-slate-600'}`}>
                <tr>
                  <th className="p-3">Stall ID</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Dimensions</th>
                  <th className="p-3">Area (sqm)</th>
                  <th className="p-3">Price (₹ INR)</th>
                  <th className="p-3">X, Y Coords</th>
                  <th className="p-3">Live DB Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isFullScreen ? 'divide-slate-700/60' : 'divide-slate-100'}`}>
                {filteredStalls.map((s) => {
                  const liveBooth = boothsMap[s.id];
                  const status = liveBooth?.status || s.initialStatus || 'available';

                  return (
                    <tr key={s.id} className={isFullScreen ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50'}>
                      <td className="p-3 font-mono font-black">{s.id}</td>
                      <td className="p-3 font-bold text-slate-400">{s.category}</td>
                      <td className="p-3 font-mono">{s.dimensions}</td>
                      <td className="p-3 font-mono">{s.areaSqm}</td>
                      <td className="p-3 font-mono">₹{s.priceInr.toLocaleString()}</td>
                      <td className="p-3 font-mono text-slate-400">{s.x}, {s.y}</td>
                      <td className="p-3">
                        <span
                          className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                            status === 'booked'
                              ? 'bg-slate-700 text-slate-300 border-slate-600'
                              : status === 'on_hold'
                              ? 'bg-amber-950 text-amber-300 border-amber-700'
                              : 'bg-emerald-950 text-emerald-300 border-emerald-700'
                          }`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedElement({ type: 'stall', id: s.id });
                            setViewMode('visual');
                          }}
                          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-bold cursor-pointer"
                        >
                          Focus in Canvas
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
export default FloorPlanBuilder;
